import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const EditEstimatePage = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    clientId: null,
    estimateNumber: '',
    estimateDate: '',
    expiryDate: '',
    lineItems: [],
    subTotal: 0,
    taxRate: 0.0825, // Default, will be overwritten by fetched data if available
    taxAmount: 0,
    totalAmount: 0,
    status: 'draft',
    notes: '',
    termsAndConditions: '',
  });
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [originalEstimateNumber, setOriginalEstimateNumber] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/users?role=client', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch clients');
        const data = await response.json();
        if (data.status === 'success') {
          setClients(data.data.users || []);
        } else {
          throw new Error(data.message || 'Could not parse client data');
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        // setError('Failed to load clients.'); // Optionally set error for client fetching
      }
    };
    fetchClients();
  }, [token]);

  useEffect(() => {
    if (!estimateId || !token) return;
    setIsLoading(true);
    fetch(`/api/estimates/${estimateId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(body => { 
            throw new Error(body.message || `Error ${res.status}`); 
          });
        }
        return res.json();
      })
      .then(payload => {
        const est = payload.data.estimate;
        setOriginalEstimateNumber(est.estimateNumber);
        setFormData({
          id: est.id,
          clientId: est.client?.id || est.clientId || null, // Ensure clientId is correctly populated
          estimateNumber: est.estimateNumber || `EST-${Date.now()}`,
          estimateDate: est.estimateDate ? new Date(est.estimateDate).toISOString().split('T')[0] : '',
          expiryDate: est.expiryDate ? new Date(est.expiryDate).toISOString().split('T')[0] : '',
          lineItems: est.lineItems.map(item => ({
            ...item,
            total: parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0)
          })),
          subTotal: parseFloat(est.subTotal || 0),
          taxRate: parseFloat(est.taxRate || 0.0825),
          taxAmount: parseFloat(est.taxAmount || 0),
          totalAmount: parseFloat(est.totalAmount || 0),
          status: est.status || 'draft',
          notes: est.notes || '',
          termsAndConditions: est.termsAndConditions || '',
        });
      })
      .catch((err) => {
        console.error('Error fetching estimate:', err);
        setError(`Failed to fetch estimate: ${err.message}`);
      })
      .finally(() => setIsLoading(false));
  }, [estimateId, token]);

  useEffect(() => {
    let currentSubTotal = 0;
    formData.lineItems.forEach(item => {
      currentSubTotal += parseFloat(item.total || 0);
    });
    const currentTaxAmount = currentSubTotal * parseFloat(formData.taxRate || 0);
    const currentTotalAmount = currentSubTotal + currentTaxAmount;

    // Only update if the calculated values differ, to avoid infinite loops if setFormData is in deps
    if (formData.subTotal !== currentSubTotal || formData.taxAmount !== currentTaxAmount || formData.totalAmount !== currentTotalAmount) {
        setFormData(prev => ({
            ...prev,
            subTotal: currentSubTotal,
            taxAmount: currentTaxAmount,
            totalAmount: currentTotalAmount,
        }));
    }
  }, [formData.lineItems, formData.taxRate]); // formData.subTotal, formData.taxAmount, formData.totalAmount removed to prevent potential loops

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value,
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value, type } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = { ...updatedLineItems[index] }; // Create a new object for the item

    item[name] = type === 'number' ? parseFloat(value) : value;

    if (name === 'quantity' || name === 'unitPrice') {
      item.total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
    }
    updatedLineItems[index] = item;
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems.splice(index, 1);
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.clientId) {
        setError('Please select a client.');
        setIsLoading(false);
        return;
    }
    if (formData.lineItems.some(item => !item.description || item.quantity <= 0 || item.unitPrice < 0)) {
        setError('Please ensure all line items have a description and positive quantity.');
        setIsLoading(false);
        return;
    }

    const payload = {
      ...formData,
      clientId: parseInt(formData.clientId, 10),
      estimateDate: new Date(formData.estimateDate).toISOString(),
      expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
      lineItems: formData.lineItems.map(item => ({
        id: item.id, // Include ID for existing items if backend supports it for updates
        description: item.description,
        quantity: parseFloat(item.quantity || 0),
        unitPrice: parseFloat(item.unitPrice || 0),
        total: parseFloat(item.total || 0),
      })),
      taxRate: parseFloat(formData.taxRate || 0),
      // subTotal, taxAmount, totalAmount are calculated but can be sent if backend expects them
    };

    try {
      const response = await fetch(`/api/estimates/${estimateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status}`);
      }
      alert('Estimate updated successfully!');
      navigate(`/admin/estimates/view/${estimateId}`);
    } catch (err) {
      console.error('Error updating estimate:', err);
      setError(err.message || 'An error occurred while updating the estimate.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const inputSmClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"; // Ensure consistent small input class

  if (isLoading && !clients.length) { // Show loading only if main data or clients are not yet loaded
    return <div className="container mx-auto px-4 py-8 text-center">Loading estimate details...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
            <h1 className="text-4xl font-bold text-brand-text-dark">Edit Estimate {originalEstimateNumber ? `#${originalEstimateNumber}` : ''}</h1>
            <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">&larr; Back</button>
        </header>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }
  
  if (!formData.id && !isLoading) { // If loading is finished and still no formData.id, means estimate not found or error during fetch handled by `error` state.
    return (
        <div className="container mx-auto px-4 py-8 text-center">
            <p>Estimate not found.</p>
            <button onClick={() => navigate("/admin/estimates")} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Go to Estimates
            </button>
        </div>
    );
}


  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-dark">Edit Estimate {originalEstimateNumber ? `#${originalEstimateNumber}` : (formData.estimateNumber || '')}</h1>
        <button onClick={() => navigate(`/admin/estimates/view/${estimateId}`)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">
          &larr; Back to View Estimate
        </button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-8">
        {/* Display general error that is not field-specific */}
        {error && !formData.clientId /* Only show general error if not a validation error shown elsewhere */ && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select
            name="clientId"
            id="clientId"
            value={formData.clientId || ''}
            onChange={handleChange}
            className={inputClass}
            required
            disabled={!clients.length} // Disable if clients haven't loaded
          >
            <option value="" disabled>Select a client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.username} ({client.email})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="estimateNumber" className="block text-sm font-medium text-gray-700 mb-1">Estimate Number</label>
            <input type="text" name="estimateNumber" id="estimateNumber" value={formData.estimateNumber} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="estimateDate" className="block text-sm font-medium text-gray-700 mb-1">Estimate Date</label>
            <input type="date" name="estimateDate" id="estimateDate" value={formData.estimateDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
            <input type="date" name="expiryDate" id="expiryDate" value={formData.expiryDate} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select name="status" id="status" value={formData.status} onChange={handleChange} className={inputClass}>
              <option value="draft">Draft</option>
              <option value="sent_to_client">Sent to Client</option>
              <option value="accepted_by_client">Accepted by Client</option>
              <option value="rejected_by_client">Rejected by Client</option>
              <option value="invoiced">Invoiced</option>
              <option value="void">Void</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
          {formData.lineItems.map((item, index) => (
            <div key={item.id || index} className="grid grid-cols-12 gap-x-3 gap-y-2 items-end p-3 mb-3 border rounded-md">
              <div className="col-span-12 sm:col-span-5">
                <label htmlFor={`itemDesc-${index}`} className="block text-xs font-medium text-gray-600">Description</label>
                <input type="text" name="description" id={`itemDesc-${index}`} value={item.description} onChange={(e) => handleLineItemChange(index, e)} className={inputSmClass} placeholder="Service or Item" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemQty-${index}`} className="block text-xs font-medium text-gray-600">Quantity</label>
                <input type="number" name="quantity" id={`itemQty-${index}`} value={item.quantity} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.01" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemPrice-${index}`} className="block text-xs font-medium text-gray-600">Unit Price</label>
                <input type="number" name="unitPrice" id={`itemPrice-${index}`} value={item.unitPrice} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.00" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemTotal-${index}`} className="block text-xs font-medium text-gray-600">Item Total</label>
                <input type="number" name="total" id={`itemTotal-${index}`} value={item.total.toFixed(2)} readOnly className={`${inputSmClass} text-right bg-gray-50`} />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                {formData.lineItems.length > 0 && (
                  <button type="button" onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700 p-1 rounded-md text-sm">Remove</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addLineItem} className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm py-2 px-3 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors">
            + Add Line Item
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6 mt-6">
          <div>
            <label htmlFor="subTotal" className="block text-sm font-medium text-gray-500">Subtotal</label>
            <input type="text" name="subTotal" id="subTotal" value={formData.subTotal.toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
          <div>
            <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700">Tax Rate</label>
            <input type="number" name="taxRate" id="taxRate" value={formData.taxRate} onChange={handleChange} className={`${inputClass} text-right`} step="0.0001" />
          </div>
          <div>
            <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-500">Tax Amount</label>
            <input type="text" name="taxAmount" id="taxAmount" value={formData.taxAmount.toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
        </div>

        <div className="text-right mb-6 border-t pt-4">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-2xl font-bold text-gray-800">${formData.totalAmount.toFixed(2)}</p>
        </div>

        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Internal notes or notes for the client..."></textarea>
        </div>

        <div className="mb-8">
          <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (Optional)</label>
          <textarea name="termsAndConditions" id="termsAndConditions" value={formData.termsAndConditions} onChange={handleChange} rows="3" className={inputClass} placeholder="Define payment terms, scope of work, etc..."></textarea>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/admin/estimates/view/${estimateId}`)}
            className="mr-3 px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="mr-3 px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          {formData.id && formData.status !== 'invoiced' && (
            <button
              type="button"
              onClick={() => {
                if (formData) {
                  navigate('/admin/invoices/new', { 
                    state: { estimateToConvert: formData } 
                  });
                }
              }}
              className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400"
              disabled={isLoading}
            >
              Convert to Invoice
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditEstimatePage;

